import { Link } from '@tanstack/react-router';
import { PLANS, SOLD_PLAN_KEYS, activePlans, highestTier, type PlanKey } from '@aeon-karaoke/cloud-convex/model/plans';
import { Button, LoadingState, MonoLabel, PageHeader, SectionHeader, buttonClassName } from '@/component';
import { useBillingModals, useBillingSummary, useCurrentUser, useMyTracksSummary, usePackShelf } from '@/hook';
import { formatUsd } from '@/lib/money';
import { LibraryPackCard, LibraryLockedPackCard } from './LibraryPackCard';

const plural = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** The note under a pack the caller cannot play: the paid plan on sale that
 * includes it, else its price. A pack only in plans no longer sold is not
 * for sale. */
export function lockedPackNote(pack: { includedInPlans: PlanKey[]; priceCents: number | null }): string {
  const plan = SOLD_PLAN_KEYS.find((key) => key !== 'free' && pack.includedInPlans.includes(key));
  if (plan) return `In ${PLANS[plan].name}`;
  return pack.priceCents === null ? 'Not for sale yet' : `Buy for ${formatUsd(pack.priceCents)}`;
}

/** /library: the shelf, not a song list. Tracks you made on top, then the
 * packs the caller can play, then the ones they cannot. Free accounts also get
 * the Beta band, which offers the one subscription on sale. */
export function LibraryPage() {
  const { user } = useCurrentUser();
  const summary = useMyTracksSummary();
  const shelf = usePackShelf();
  const billing = useBillingSummary();
  const { openCheckout } = useBillingModals();
  const subscribed = user ? highestTier(activePlans(user)) !== 'free' : false;

  const made = summary?.all ?? 0;
  const inPlan = shelf?.inPlan.length ?? 0;
  const eyebrow = `${plural(made, 'track')} you made · ${
    subscribed ? `${plural(inPlan, 'pack')} in your plan` : `${inPlan} starter ${inPlan === 1 ? 'pack' : 'packs'}`
  }`;
  const counts = [
    { n: summary?.ready ?? 0, label: 'Ready to sing', tone: 'text-up' },
    { n: summary?.preparing ?? 0, label: 'Preparing', tone: 'text-acc' },
    { n: summary?.attention ?? 0, label: 'Needs attention', tone: 'text-down' },
  ];

  return (
    <div>
      <PageHeader
        eyebrow={eyebrow}
        title="Library"
        action={
          <Link to="/create" className={buttonClassName({ className: 'px-[21px] py-[13px]' })}>
            Create karaoke track
          </Link>
        }
      />

      <Link
        to="/library/mine"
        className="mt-11 flex flex-wrap items-center gap-8 rounded-lg border border-accFill bg-surface p-7 text-ink"
      >
        <div className="min-w-[260px] flex-1">
          <MonoLabel tone="acc" className="block tracking-[0.18em]">
            Yours
          </MonoLabel>
          <div className="mt-[10px] text-[28px] font-semibold tracking-[-0.03em]">Tracks you made</div>
          <p className="mt-2 max-w-[46ch] text-[15px] leading-[1.5] text-mute">
            Rename, replace the audio, change who can queue them, or delete. Anything still being built lives here
            too.
          </p>
        </div>
        <div className="flex flex-wrap gap-9">
          {counts.map((count) => (
            <div key={count.label}>
              <div className={`text-[30px] font-semibold tabular-nums tracking-[-0.03em] ${count.tone}`}>{count.n}</div>
              <MonoLabel className="mt-[6px] block">{count.label}</MonoLabel>
            </div>
          ))}
        </div>
      </Link>

      {user && !subscribed && (
        <div className="mt-[52px] flex flex-wrap items-center justify-between gap-7 rounded-lg border border-accFill bg-surface px-7 py-[26px]">
          <div className="min-w-[280px] flex-1">
            <MonoLabel tone="acc" className="block tracking-[0.18em]">
              {`${PLANS.beta.name} · ${formatUsd(PLANS.beta.priceCents.month)} a month`}
            </MonoLabel>
            <p className="mt-[10px] max-w-[30ch] text-[22px] font-semibold leading-[1.3] tracking-[-0.025em]">
              {`Every pack in ${PLANS.beta.name}, ${PLANS.beta.monthlyCreateLimit} uploads a month.`}
            </p>
            <p className="mt-2 max-w-[46ch] text-[15px] leading-[1.5] text-mute">
              {`Up from ${PLANS.free.monthlyCreateLimit} uploads a month on ${PLANS.free.name}. Cancel any time from settings.`}
            </p>
          </div>
          <div className="flex flex-wrap gap-[10px]">
            {billing?.checkout.open && (
              <Button className="px-5 py-[13px]" onClick={() => openCheckout({ kind: 'beta' })}>
                Upgrade
              </Button>
            )}
            <Link to="/settings/$tab" params={{ tab: 'plan' }} className={buttonClassName({ variant: 'outline', className: 'px-[18px] py-[13px]' })}>
              See plans
            </Link>
          </div>
        </div>
      )}

      <SectionHeader title={subscribed ? 'Packs in your plan' : 'Your starter packs'} className="mt-[52px]" />
      {!shelf ? (
        <LoadingState />
      ) : shelf.inPlan.length === 0 ? (
        <p className="mt-[22px] text-sm text-mute">No packs yet.</p>
      ) : (
        <div className="mt-[22px] grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-[18px]">
          {shelf.inPlan.map((pack) => (
            <LibraryPackCard key={pack._id} pack={pack} />
          ))}
        </div>
      )}

      {shelf && shelf.notInPlan.length > 0 && (
        <>
          <SectionHeader title="Not in your plan" className="mt-[52px]" />
          <div className="mt-[22px] grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-[18px]">
            {shelf.notInPlan.map((pack) => (
              <LibraryLockedPackCard key={pack._id} pack={pack} note={lockedPackNote(pack)} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
